import React from 'react';
import { motion } from 'motion/react';
import { X, Bot, User, Zap, Tag, Activity, MessageSquare } from 'lucide-react';
import { GlassCard } from './SharedUI';
import { useT } from '../lib/useT';

export interface AgentPulseLite {
  level: number;
  active: boolean;
  recentMessages: number;
  status?: string;
  lastActiveAt?: string | null;
}

interface AgentDetailCardProps {
  agent: any;
  onClose: () => void;
  onChat?: (agent: any) => void;
  // F 实时脉冲数据（来自 useNexusPulse），驱动实时状态徽章与活跃度
  pulse?: AgentPulseLite;
  memoryCount?: number;
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '从未';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '未知';
  const diff = Date.now() - ts;
  if (diff < 1000) return '刚刚';
  if (diff < 60000) return `${Math.floor(diff / 1000)} 秒前`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  return `${Math.floor(diff / 86400000)} 天前`;
}

function statusMeta(status?: string): { label: string; color: string } {
  switch (status) {
    case 'active': return { label: '在线', color: 'text-emerald-400' };
    case 'busy': return { label: '忙碌', color: 'text-amber-400' };
    case 'idle': return { label: '空闲', color: 'text-sky-400' };
    case 'terminated': return { label: '已停用', color: 'text-white/40' };
    default: return { label: '离线', color: 'text-white/40' };
  }
}

export function AgentDetailCard({ agent, onClose, onChat, pulse, memoryCount }: AgentDetailCardProps) {
  const isZh = useT().langCode !== 'en';
  if (!agent) return null;
  const name = agent.name || agent.label || '未命名智能体';
  const rawStatus = pulse?.status ?? agent.status;
  const meta = statusMeta(rawStatus);
  const category = agent.category || (isZh ? '未分类' : 'Uncategorized');
  const personality = agent.personalityId || (isZh ? '默认人格' : 'Default');
  const desc = agent.description || agent.prompt || (isZh ? '暂无简介' : 'No description');

  const level = typeof pulse?.level === 'number' ? pulse.level : (rawStatus === 'active' ? 0.6 : 0.15);
  const recent = pulse?.recentMessages ?? 0;
  const lastActive = relativeTime(pulse?.lastActiveAt ?? agent.lastActiveAt);

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      className="absolute right-8 top-24 w-80 z-40 pointer-events-auto"
    >
      <GlassCard className="p-5 rounded-[1.5rem] space-y-4 border-white/5 bg-black/40 backdrop-blur-3xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-celestial-mars to-celestial-saturn flex items-center justify-center shadow-lg shadow-celestial-saturn/20">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-black text-white/90 leading-tight">{name}</div>
              <div className={`text-xs font-bold mt-0.5 flex items-center gap-1.5 ${meta.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${rawStatus === 'active' || rawStatus === 'busy' ? 'bg-current animate-pulse' : 'bg-current/50'}`} />
                {meta.label}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/50 hover:text-white transition-colors"
            aria-label="关闭"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-2.5 text-xs">
          <div className="flex items-center gap-2 text-white/55">
            <Tag size={13} className="text-celestial-saturn" />
            <span className="text-white/40 w-14 shrink-0">{isZh ? '分类' : 'Category'}</span>
            <span className="font-bold text-white/80">{category}</span>
          </div>
          <div className="flex items-center gap-2 text-white/55">
            <User size={13} className="text-celestial-saturn" />
            <span className="text-white/40 w-14 shrink-0">{isZh ? '人格' : 'Persona'}</span>
            <span className="font-bold text-white/80 truncate">{personality}</span>
          </div>
          <div className="flex items-start gap-2 text-white/55">
            <Zap size={13} className="text-celestial-saturn mt-0.5" />
            <span className="text-white/40 w-14 shrink-0">{isZh ? '简介' : 'About'}</span>
            <span className="font-medium text-white/70 leading-relaxed">{desc}</span>
          </div>
        </div>

        {/* F 实时活跃度 */}
        <div className="space-y-2 rounded-xl bg-white/5 p-3">
          <div className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5 text-white/50">
              <Activity size={12} className="text-celestial-saturn" /> 实时活跃度
            </span>
            <span className="font-mono text-white/70">{Math.round(level * 100)}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-celestial-saturn to-emerald-400"
              animate={{ width: `${Math.round(level * 100)}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-white/50">
            <span className="flex items-center gap-1.5">
              <MessageSquare size={12} className={recent > 0 ? 'text-emerald-400' : 'text-white/30'} />
              近 10 秒消息
            </span>
            <span className={`font-mono ${recent > 0 ? 'text-emerald-400 font-bold' : 'text-white/50'}`}>{recent}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-white/50">
            <span>最近活跃</span>
            <span className="font-mono text-white/60">{lastActive}</span>
          </div>
          {typeof memoryCount === 'number' && (
            <div className="flex items-center justify-between text-[11px] text-white/50">
              <span>记忆条数</span>
              <span className="font-mono text-white/60">{memoryCount}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => onChat?.(agent)}
          className="w-full py-2.5 rounded-xl bg-celestial-saturn/20 hover:bg-celestial-saturn/30 border border-celestial-saturn/30 text-celestial-saturn text-xs font-black tracking-wider uppercase transition-colors"
        >
          {isZh ? '开始对话' : 'START CHAT'}
        </button>
      </GlassCard>
    </motion.div>
  );
}
